// src/pages/UserRequestPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select, { type StylesConfig } from 'react-select';
import userRequestService, {
  type Group,
  type GroupSecurityGroup,
  type PersonData,
} from '@services/userRequestService';

const comboBoxSelectStyles: StylesConfig<any, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    height: '32px',
    borderRadius: '2px',
    boxShadow: 'none',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    borderColor: state.isFocused ? '#66afe9' : '#ccc',
    '&:hover': { borderColor: '#66afe9' },
  }),
  valueContainer: base => ({ ...base, height: '32px', padding: '0 8px' }),
  input: base => ({ ...base, margin: 0, padding: 0, lineHeight: 'normal' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, padding: '4px 6px' }),
  clearIndicator: base => ({ ...base, padding: '4px' }),
  placeholder: base => ({ ...base, fontSize: '12px', lineHeight: 'normal', color: '#aaa' }),
  singleValue: base => ({
    ...base,
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 'normal',
    color: 'rgb(158, 149, 135)',
  }),
  menuPortal: base => ({ ...base, zIndex: 1000200 }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: '2px',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
  }),
  menuList: base => ({ ...base, padding: 0, maxHeight: '200px' }),
  option: (_base, state) => ({
    display: 'block',
    boxSizing: 'border-box' as const,
    width: '100%',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: '1.4',
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#62accb' : '#fff',
    color: state.isFocused ? '#fff' : '#333',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

const UserRequestPage: React.FC = () =>
{
  const { personId } = useParams<{ personId: string }>();

  const [person, setPerson] = useState<PersonData | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [approving, setApproving] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [selectedSubGroup, setSelectedSubGroup] = useState<GroupSecurityGroup | null>(null);
  const [selectedDefaultGroup, setSelectedDefaultGroup] = useState<Group | null>(null);

  useEffect(() =>
  {
    document.title = 'User Request';
    if (!personId) return;

    const id = parseInt(personId, 10);
    Promise.all([userRequestService.getPersonById(id), userRequestService.getGroups()])
      .then(([personData, groupData]) =>
      {
        setPerson(personData);
        setGroups(groupData);
      })
      .catch(err => console.error('Error loading user request data:', err))
      .finally(() => setLoading(false));
  }, [personId]);

  const handleGroupChange = (option: Group | null) =>
  {
    setSelectedGroup(option);
    setSelectedSubGroup(null);
  };

  const handleSubGroupChange = (option: GroupSecurityGroup | null) =>
  {
    setSelectedSubGroup(option);
  };

  const handleDefaultGroupChange = (option: Group | null) =>
  {
    setSelectedDefaultGroup(option);
  };

  const handleApprove = async () =>
  {
    if (!person || !selectedGroup || !selectedDefaultGroup) return;

    setApproving(true);
    try
    {
      await userRequestService.approveUser({
        PersonID: String(person.PersonID),
        ConfigValueID: person.ConfigValueID,
        Email: person.Email,
        SecurityGroups: selectedSubGroup
          ? [
            {
              GroupId: selectedGroup.GroupId,
              GroupName: selectedGroup.Name,
              Name: selectedSubGroup.Name,
              SecurityGroupId: selectedSubGroup.SecurityGroupId,
            },
          ]
          : [],
        DefaultGroup: {
          ...selectedDefaultGroup,
          SelectedSubGroup: selectedSubGroup,
        },
      });
      setApproved(true);
    }
    catch (err)
    {
      console.error('Error approving user:', err);
    }
    finally
    {
      setApproving(false);
    }
  };

  const showSuccess = approved || person?.IsEnabled === true;
  const canApprove = !approving && !!selectedGroup && !!selectedDefaultGroup;

  if (loading)
  {
    return (
      <div className="p-6">
        <h2 className="text-lg font-semibold text-[#5697b3] mb-4">User Request</h2>
        <div className="text-xs text-brightGray">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold text-[#5697b3] mb-4">User Request</h2>

      <div className="bg-white border border-darkGray rounded max-w-2xl p-5">
        {showSuccess ? (
          <p className="text-sm text-corgeeblack">User is successfully approved.</p>
        ) : (
          <>
            {/* Person info */}
            <div className="mb-4">
              <div className="text-sm font-semibold text-corgeeblack">{person?.Name}</div>
              <div className="text-xs text-brightGray mt-0.5">{person?.Email}</div>
            </div>

            <hr className="border-corgeedashgray mb-4" />

            {/* Security Groups */}
            <div className="mb-4">
              <div className="text-sm font-medium text-corgeeblue mb-0.5">
                Select this user's Security Groups
              </div>
              <div className="text-xs text-brightGray mb-2">
                All their new items will be created in this compartment
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Select
                    options={groups}
                    value={selectedGroup}
                    onChange={handleGroupChange}
                    getOptionLabel={(g: Group) => g.Name}
                    getOptionValue={(g: Group) => String(g.GroupId)}
                    placeholder="Security Group"
                    isSearchable
                    isClearable
                    styles={comboBoxSelectStyles}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
                <div className="flex-1">
                  <Select
                    options={selectedGroup?.GroupSecurityGroups ?? []}
                    value={selectedSubGroup}
                    onChange={handleSubGroupChange}
                    getOptionLabel={(sg: GroupSecurityGroup) => sg.Name}
                    getOptionValue={(sg: GroupSecurityGroup) => String(sg.SecurityGroupId)}
                    placeholder="Security Sub-Group"
                    isSearchable
                    isClearable
                    isDisabled={!selectedGroup}
                    styles={comboBoxSelectStyles}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>

            {/* Default Group */}
            <div className="mb-5">
              <div className="text-sm font-medium text-corgeeblue mb-2">
                Select this user's Default Group
              </div>
              <div className="w-56">
                <Select
                  options={groups}
                  value={selectedDefaultGroup}
                  onChange={handleDefaultGroupChange}
                  getOptionLabel={(g: Group) => g.Name}
                  getOptionValue={(g: Group) => String(g.GroupId)}
                  placeholder="Security Default-Group"
                  isSearchable
                  isClearable
                  styles={comboBoxSelectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>
            </div>

            {/* Approve button */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleApprove}
                disabled={!canApprove}
                className="bg-corgeeblue hover:bg-corgeelueHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {approving ? 'Approving...' : 'Approve Request'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserRequestPage;
